/* eslint-disable @typescript-eslint/ban-ts-comment */

import type { ZillowLoginBaseOptions } from '.';
import type { GetZillowWebLoginURLOptions } from '@zillow/auth-core';

// @ts-ignore: local package uses declaration files, ignore for external consumers
import { getZillowWebLoginURL } from '@zillow/auth-core/dist/es/zoam-web-sdk/utils/getZillowWebLoginURL';

export type ZillowLoginUrlOptions = ZillowLoginBaseOptions & GetZillowWebLoginURLOptions;

/**
 * Returns a Zillow login URL string for use in server-side rendering (e.g.
 * `getServerSideProps`).
 *
 * @note does not work well in development due to browser limitations; use `rjs-ext` login instead
 * @note `baseURL` must be an absolute URL — unlike the client helpers, there is no `window.location`
 * to resolve a relative value against, so use a non-public, always-absolute env var (e.g. `AUTH_BASE_URL`)
 * rather than the `NEXT_PUBLIC_AUTH_BASE_URL` used by client-side login helpers.
 *
 * @example
 * ```typescript
 * export const getServerSideProps = (async (context: GetServerSidePropsContext) => {
 *   const user = await fetchUser();
 *
 *   if (!user) {
 *     return {
 *       redirect: {
 *         destination: zillowLoginUrl({
 *           baseURL: process.env.AUTH_BASE_URL,
 *           entryPoint: 'example-web',
 *           redirectUri: `/example-web${context.resolvedUrl}`
 *         })
 *       }
 *     };
 *   }
 *
 *   return { props: { ... } };
 * }) satisfies GetServerSideProps;
 * ```
 */
export function zillowLoginUrl({ disableForceLogin, ...options }: ZillowLoginUrlOptions): `${string}:${string}` {
  return getZillowWebLoginURL({
    forceLogin: !disableForceLogin,
    ...options,
  }) as `${string}:${string}`;
}
